import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import {
  Button,
  Select,
  Row,
  Input,
  Col,
  Table,
  DatePicker,
  Checkbox,
} from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import startCase from 'lodash/startCase';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import InputLeftGrid from '../Components/InputLeftGrid';
import InputRightGrid from '../Components/InputRightGrid';
import InputCentreGrid from '../Components/InputCentreGrid';
import {
  getCustomers,
  clearCustomers,
  clearProductIds,
  getProductIds,
  getActiveJobchartIds,
  clearActiveJobchartIds,
  clearCurrentDepartmentInfoForJobchart,
  getCurrentDepartmentInfoForJobchart,
  clearUsers,
  getUsers,
  getFactoryIds,
  clearFactoryIds,
  clearJobchartPreview,
  getJobchartPreview,
  getRackIds,
  clearRackIds,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import constants from '../constants';
import ItemForm from './ItemForm';

const AntInput = GetField(Input);
const AntSelect = GetField(Select);
const AntDatePicker = GetField(DatePicker);
const AntCheckbox = GetField(Checkbox);
const { Option } = Select;

const IssueReceive = props => {
  const { classes } = props;
  const history = useHistory();
  const dispatch = useDispatch();

  const { handleSubmit, control, getValues, watch, setValue } = useForm({
    defaultValues: {
      createdAt: new Date().toISOString(),
      items: [{}],
      isAlter: false,
    },
  });

  const { fields: fabricFields } = useFieldArray({
    control,
    name: 'fabrics',
  });

  const { fields: styleFields } = useFieldArray({
    control,
    name: 'styles',
  });

  const { fields: accessoryFields } = useFieldArray({
    control,
    name: 'accessories',
  });

  const {
    fields: itemFields,
    append: itemAppend,
    remove: itemRemove,
  } = useFieldArray({
    control,
    name: 'items',
  });

  const customers = useSelector(state => state.customers);
  const rackIds = useSelector(state => state.rackIds);
  const productIds = useSelector(state => state.productIds);
  const activeJobchartIds = useSelector(state => state.activeJobchartIds);
  const currentDepartmentInfoForJobchart = useSelector(
    state => state.currentDepartmentInfoForJobchart
  );
  const users = useSelector(state => state.users);
  const factoryIds = useSelector(state => state.factoryIds);
  const jobchartPreview = useSelector(state => state.jobchartPreview);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [fromProcesses, setFromProcesses] = useState([]);
  const [toProcesses, setToProcesses] = useState([]);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getUsers());
    dispatch(getFactoryIds());
    dispatch(getRackIds());

    return () => {
      dispatch(clearCustomers());
      dispatch(clearUsers());
      dispatch(clearFactoryIds());
      dispatch(clearJobchartPreview());
      dispatch(clearRackIds());
    };
  }, []);

  useEffect(() => {
    const customerId = getValues('customerId');
    if (!customerId) {
      dispatch(clearProductIds());
      setValue('productId', null);
    } else {
      dispatch(getProductIds(customerId));
    }
  }, [watch('customerId')]);

  useEffect(() => {
    const productId = getValues('productId');
    if (!productId) {
      dispatch(clearActiveJobchartIds());
      setValue('jobchartId', null);
    } else {
      dispatch(getActiveJobchartIds({ productId }));
    }
  }, [watch('productId')]);

  useEffect(() => {
    const _fromDepartment = getValues('fromDepartment');
    if (_fromDepartment) {
      setValue('fromProcess', null);

      setFromProcesses(
        Object.values(constants.departmentProcesses[_fromDepartment])
      );
    }
  }, [watch('fromDepartment')]);

  useEffect(() => {
    const _toDepartment = getValues('toDepartment');
    if (_toDepartment) {
      setValue('toProcess', null);

      setToProcesses(
        Object.values(constants.departmentProcesses[_toDepartment])
      );
    }
  }, [watch('toDepartment')]);

  useEffect(() => {
    const customerId = getValues('customerId');
    const productId = getValues('productId');
    const jobchartId = getValues('jobchartId');
    const fromDepartment = getValues('fromDepartment');
    const fromProcess = getValues('fromProcess');
    const fromFactoryId = getValues('fromFactoryId');
    if (
      customerId &&
      productId &&
      jobchartId &&
      fromDepartment &&
      fromProcess &&
      fromFactoryId
    ) {
      dispatch(
        getCurrentDepartmentInfoForJobchart(
          jobchartId,
          fromProcess,
          fromFactoryId
        )
      );
    }
    return () => {
      dispatch(clearCurrentDepartmentInfoForJobchart());
    };
  }, [
    watch('customerId'),
    watch('productId'),
    watch('jobchartId'),
    watch('fromDepartment'),
    watch('fromProcess'),
    watch('fromFactoryId'),
  ]);

  useEffect(() => {
    setValue('fabrics', currentDepartmentInfoForJobchart.fabrics);
    setValue('styles', currentDepartmentInfoForJobchart.styles);
    setValue('accessories', currentDepartmentInfoForJobchart.accessories);
  }, [currentDepartmentInfoForJobchart]);

  useEffect(() => {
    const jobchartId = getValues('jobchartId');
    if (jobchartId) {
      dispatch(getJobchartPreview(jobchartId));
    }
    return () => {};
  }, [watch('jobchartId')]);

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post('/issuereceive', formValues);
      successNotification('Issued');
      history.push('/issuereceive/challan');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  const rowKey = useCallback(
    record =>
      `Id${record.id}P${record.product}F${record.fabric}W${record.width}B${
        record.baseColor
      }P${record.printColor || ''}` ||
      `Id${record.id}P${record.product}A${record.accessory}` ||
      `Id${record.id}P${record.product}S${record.name}`,
    []
  );

  const stylesSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
    ];

    return _columns;
  }, []);

  const fabricsSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      // {
      //   title: 'Stock',
      //   dataIndex: 'stock',
      // },
    ];

    return _columns;
  }, []);

  const fabricsSummaryWithoutProductColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      // {
      //   title: 'Stock',
      //   dataIndex: 'stock',
      // },
    ];

    return _columns;
  }, []);

  const accessoriesSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      // {
      //   title: 'Stock',
      //   dataIndex: 'stock',
      // },
    ];

    return _columns;
  }, []);

  return (
    <>
      <Breadcrumbs
        moduleName="Issue Receive"
        moduleLink="/issuereceive/challan"
        subModuleName="Form"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="productId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="Product"
                  placeholder="Select product"
                  {...field}
                >
                  {productIds.map(product => (
                    <Option key={product.id} value={product.id}>
                      {product.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="jobchartId"
              control={control}
              render={({ field }) => (
                <AntSelect label="BPO #" placeholder="Select bpo" {...field}>
                  {activeJobchartIds.map(jobchart => (
                    <Option key={jobchart.id} value={jobchart.id}>
                      {jobchart.bpoNo}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="toUserId"
              control={control}
              render={({ field }) => (
                <AntSelect label="To User" placeholder="Select user" {...field}>
                  {users.map(user => (
                    <Option key={user.id} value={user.id}>
                      {user.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="createdAt"
              control={control}
              render={({ field }) => (
                <AntDatePicker label="Date" allowClear={false} {...field} />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="isAlter"
              control={control}
              render={({ field }) => (
                <AntCheckbox label="Is Alter?" {...field} />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <InputLeftGrid>
            <Controller
              name="fromDepartment"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="From Department"
                  placeholder="Select department"
                  {...field}
                >
                  {Object.keys(constants.departmentProcesses).map(
                    department => (
                      <Option key={department} value={department}>
                        {startCase(department)}
                      </Option>
                    )
                  )}
                </AntSelect>
              )}
            />
          </InputLeftGrid>
          <InputCentreGrid>
            <Controller
              name="fromProcess"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="From Process"
                  placeholder="Select process"
                  {...field}
                >
                  {fromProcesses.map(process => (
                    <Option key={process} value={process}>
                      {startCase(process)}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </InputCentreGrid>
          <InputRightGrid>
            <Controller
              name="fromFactoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="From Factory"
                  placeholder="Select factory"
                  {...field}
                >
                  {factoryIds.map(factory => (
                    <Option key={factory.id} value={factory.id}>
                      {factory.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </InputRightGrid>
        </Row>
        <Row>
          <InputLeftGrid>
            <Controller
              name="toDepartment"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="To Department"
                  placeholder="Select department"
                  {...field}
                >
                  {Object.keys(constants.departmentProcesses).map(
                    department => (
                      <Option key={department} value={department}>
                        {startCase(department)}
                      </Option>
                    )
                  )}
                </AntSelect>
              )}
            />
          </InputLeftGrid>
          <InputCentreGrid>
            <Controller
              name="toProcess"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="To Process"
                  placeholder="Select process"
                  {...field}
                >
                  {toProcesses.map(process => (
                    <Option key={process} value={process}>
                      {startCase(process)}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </InputCentreGrid>
          <InputRightGrid>
            <Controller
              name="toFactoryId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  label="To Factory"
                  placeholder="Select factory"
                  {...field}
                >
                  {factoryIds.map(factory => (
                    <Option key={factory.id} value={factory.id}>
                      {factory.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </InputRightGrid>
        </Row>
        <br />
        <div className={classes.previewMainHeading}>Jobchart Table</div>
        <Row>
          <Col xs={{ span: 22, offset: 1 }}>
            {jobchartPreview.stylesSummary &&
            jobchartPreview.stylesSummary.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={stylesSummaryColumns}
                  dataSource={jobchartPreview.stylesSummary}
                  rowKey={rowKey}
                  pagination={false}
                  title={() => 'Product-Styles Summary'}
                />
              </>
            ) : null}
            {jobchartPreview.fabricsSummary &&
            jobchartPreview.fabricsSummary.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={fabricsSummaryColumns}
                  dataSource={jobchartPreview.fabricsSummary}
                  rowKey={rowKey}
                  pagination={false}
                  title={() => 'Product-Fabrics Summary'}
                />
              </>
            ) : null}
            {jobchartPreview.fabricsSummaryWithoutProduct &&
            jobchartPreview.fabricsSummaryWithoutProduct.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={fabricsSummaryWithoutProductColumns}
                  dataSource={jobchartPreview.fabricsSummaryWithoutProduct}
                  rowKey={rowKey}
                  pagination={false}
                  title={() => 'Fabrics Summary'}
                />
              </>
            ) : null}
            {jobchartPreview.accessoriesSummary &&
            jobchartPreview.accessoriesSummary.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={accessoriesSummaryColumns}
                  dataSource={jobchartPreview.accessoriesSummary}
                  rowKey={rowKey}
                  pagination={false}
                  title={() => 'Accessories Summary'}
                />
              </>
            ) : null}
          </Col>
        </Row>
        <br />
        {currentDepartmentInfoForJobchart.fabrics.length ? (
          <>
            <div className={classes.previewSubHeading}>Fabrics Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Fabric</th>
                      <th className={classes.inputTh}>Width</th>
                      <th className={classes.inputTh}>Base Color</th>
                      <th className={classes.inputTh}>Print Color</th>
                      {watch('fromProcess') ===
                      constants.departmentProcesses.store.inventory ? (
                        <th className={classes.inputTh}>Rack #</th>
                      ) : null}
                      <th className={classes.inputTh}>Jobchart Qty</th>
                      <th className={classes.inputTh}>Stock</th>
                      <th className={classes.inputTh}>Issue Qty</th>
                      {watch('toProcess') ===
                      constants.departmentProcesses.store.inventory ? (
                        <th className={classes.inputTh}>To Rack #</th>
                      ) : null}
                      <th className={classes.inputTh}>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fabricFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.fabric}</td>
                        <td className={classes.inputTd}>{item.width}</td>
                        <td className={classes.inputTd}>{item.baseColor}</td>
                        <td className={classes.inputTd}>{item.printColor}</td>
                        {watch('fromProcess') ===
                        constants.departmentProcesses.store.inventory ? (
                          <td className={classes.inputTd}>{item.rackno}</td>
                        ) : null}
                        <td className={classes.inputTd}>
                          {item.jobchartQuantity}
                        </td>
                        <td className={classes.inputTd}>{item.inventory}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                type="number"
                                min={0}
                                placeholder="Issue Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        {watch('toProcess') ===
                        constants.departmentProcesses.store.inventory ? (
                          <td className={classes.inputTd}>
                            <Controller
                              name={`fabrics[${index}].racknoId`}
                              control={control}
                              render={({ field }) => (
                                <AntSelect
                                  placeholder="Target Rack #"
                                  {...field}
                                >
                                  {rackIds.map(rack => (
                                    <Option key={rack.id} value={rack.id}>
                                      {rack.rackno}
                                    </Option>
                                  ))}
                                </AntSelect>
                              )}
                            />
                          </td>
                        ) : null}
                        <td className={classes.inputTd}>
                          <Controller
                            name={`fabrics[${index}].remark`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                placeholder="Remark"
                                {...field}
                              />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {currentDepartmentInfoForJobchart.styles.length ? (
          <>
            <div className={classes.previewSubHeading}>Styles Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Style</th>
                      <th className={classes.inputTh}>Description</th>
                      <th className={classes.inputTh}>Size</th>
                      <th className={classes.inputTh}>Base Color</th>
                      <th className={classes.inputTh}>Print Color</th>
                      <th className={classes.inputTh}>Embroidery Color</th>
                      <th className={classes.inputTh}>Acid Wash Color</th>
                      {/* <th className={classes.inputTh}>Jobchart Qty</th> */}
                      <th className={classes.inputTh}>Stock</th>
                      <th className={classes.inputTh}>Issue Qty</th>
                      <th className={classes.inputTh}>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {styleFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.name}</td>
                        <td className={classes.inputTd}>{item.description}</td>
                        <td className={classes.inputTd}>
                          {item.size} {item.unit}
                        </td>
                        <td className={classes.inputTd}>{item.baseColor}</td>
                        <td className={classes.inputTd}>{item.printColor}</td>
                        <td className={classes.inputTd}>
                          {item.embroideryColor}
                        </td>
                        <td className={classes.inputTd}>
                          {item.acidWashColor}
                        </td>
                        {/* <td className={classes.inputTd}>
                          {item.jobchartQuantity}
                        </td> */}
                        <td className={classes.inputTd}>{item.inventory}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                type="number"
                                min={0}
                                placeholder="Issue Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`styles[${index}].remark`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                placeholder="Remark"
                                {...field}
                              />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}
        {currentDepartmentInfoForJobchart.accessories.length ? (
          <>
            <div className={classes.previewSubHeading}>Accessories Table</div>
            <Row>
              <Col className={classes.tableStyle}>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Product</th>
                      <th className={classes.inputTh}>Accessory</th>
                      {watch('fromProcess') ===
                      constants.departmentProcesses.store.inventory ? (
                        <th className={classes.inputTh}>Rack #</th>
                      ) : null}
                      <th className={classes.inputTh}>Jobchart Qty</th>
                      <th className={classes.inputTh}>Stock</th>
                      <th className={classes.inputTh}>Issue Qty</th>
                      {watch('toProcess') ===
                      constants.departmentProcesses.store.inventory ? (
                        <th className={classes.inputTh}>To Rack #</th>
                      ) : null}
                      <th className={classes.inputTh}>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessoryFields.map((item, index) => (
                      <tr key={item.id}>
                        <td className={classes.inputTd}>{item.product}</td>
                        <td className={classes.inputTd}>{item.accessory}</td>
                        {watch('fromProcess') ===
                        constants.departmentProcesses.store.inventory ? (
                          <td className={classes.inputTd}>{item.rackno}</td>
                        ) : null}
                        <td className={classes.inputTd}>
                          {item.jobchartQuantity}
                        </td>
                        <td className={classes.inputTd}>{item.inventory}</td>
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].quantity`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                type="number"
                                min={0}
                                placeholder="Issue Qty"
                                {...field}
                              />
                            )}
                          />
                        </td>
                        {watch('toProcess') ===
                        constants.departmentProcesses.store.inventory ? (
                          <td className={classes.inputTd}>
                            <Controller
                              name={`accessories[${index}].racknoId`}
                              control={control}
                              render={({ field }) => (
                                <AntSelect
                                  placeholder="Target Rack #"
                                  {...field}
                                >
                                  {rackIds.map(rack => (
                                    <Option key={rack.id} value={rack.id}>
                                      {rack.rackno}
                                    </Option>
                                  ))}
                                </AntSelect>
                              )}
                            />
                          </td>
                        ) : null}
                        <td className={classes.inputTd}>
                          <Controller
                            name={`accessories[${index}].remark`}
                            control={control}
                            render={({ field }) => (
                              <AntInput
                                size="small"
                                placeholder="Remark"
                                {...field}
                              />
                            )}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Col>
            </Row>
            <br />
          </>
        ) : null}

        <div className={classes.previewSubHeading}>Items Table</div>
        <Row>
          <Col className={classes.tableStyle}>
            <table className={classes.inputTable}>
              <thead className={classes.inputThead}>
                <tr>
                  <th className={classes.inputTh}>Item</th>
                  <th className={classes.inputTh}>Shade</th>
                  <th className={classes.inputTh}>Size</th>
                  <th className={classes.inputTh}>Unit</th>
                  <th className={classes.inputTh}>Quantity</th>
                  <th className={classes.inputTh}>Remark</th>
                  <th className={classes.inputTh} style={{ width: '6%' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {itemFields.map((item, index) => (
                  <ItemForm
                    key={item.id}
                    itemIndex={index}
                    itemAppend={itemAppend}
                    itemRemove={itemRemove}
                    control={control}
                  />
                ))}
              </tbody>
            </table>
          </Col>
        </Row>
        <br />

        <Row className={classes.buttonContainer}>
          {!currentDepartmentInfoForJobchart.fabrics.length &&
          !currentDepartmentInfoForJobchart.styles.length &&
          !currentDepartmentInfoForJobchart.accessories.length &&
          !watch('items')?.length ? (
            'No data available'
          ) : (
            <Button type="primary" loading={confirmLoading} htmlType="submit">
              Submit
            </Button>
          )}
        </Row>
      </form>
    </>
  );
};

IssueReceive.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(IssueReceive);
